<template>
  <BaseSelect
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :disabled="disabled"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :selected-as-card="selectedAsCard"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  >
    <template #selected-card="{ selectedOption, clearSelection }">
      <OrderEntityLink
        :order="selectedEntity || selectedOption?.entity"
        relation-card
        :removable="clearable"
        :disabled="disabled"
        @remove="clearSelection"
      />
    </template>
  </BaseSelect>
</template>

<script>
import { mapState } from "pinia";

import { useOrderStore } from "@/stores/orderStore";
import { getCompanyId } from "@/utils/userUtils";
import ordersApi from "@/services/api/ordersApi";
import OrderEntityLink from "@/components/Order/OrderEntityLink.vue";

export default {
  name: "OrderSelect",

  components: {
    OrderEntityLink,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите Заказ",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить новый заказ",
    },
    noResultsText: {
      type: String,
      default: "Заказ не найден",
    },
    autoSelectFirst: {
      type: Boolean,
      default: false,
    },
    selectedAsCard: {
      type: Boolean,
      default: false,
    },
    selectedEntity: {
      type: Object,
      default: null,
    },
  },

  emits: ["update:modelValue", "change", "select", "button-click"],

  data() {
    return {
      companyId: getCompanyId(),
      isFirstLoad: true,
      preselectedOption: null,
      isSearchActive: false,
    };
  },

  computed: {
    ...mapState(useOrderStore, [
      "orders",
      "pagination",
      "dataIsLoading",
    ]),

    formattedOptions() {
      const options = this.orders.map((order) => ({
        value: order.id,
        label: order.selector_name,
        entity: order,
      }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    formattedOptions: {
      handler(newOptions) {
        if (
          this.autoSelectFirst &&
          this.isFirstLoad &&
          newOptions.length > 0 &&
          !this.modelValue
        ) {
          this.$nextTick(() => {
            const firstValue = newOptions[0].value;
            this.$emit("update:modelValue", firstValue);
            this.$emit("change", firstValue);
            this.isFirstLoad = false;
          });
        }
      },
      immediate: true,
    },

    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
          entity: option.entity,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
      this.$emit("select", option?.entity || null);
    },

    async handleSearch(searchData) {
      this.isFirstLoad = false;
      this.isSearchActive = true;
      const store = useOrderStore();
      await store.fetchOrders({ search: searchData.query });
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      const store = useOrderStore();
      await store.fetchOrders();
    },

    async handleScrollEnd() {
      const store = useOrderStore();
      if (
        store.pagination.page < store.pagination.pages &&
        !store.dataIsLoading
      ) {
        await store.loadMoreOrders();
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const store = useOrderStore();
      const exists = store.orders.find(
        (o) => String(o.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const response = await ordersApi.show(
          this.companyId,
          this.modelValue
        );
        const order = response.data.result;
        this.preselectedOption = {
          value: order.id,
          label: order.selector_name,
          entity: order,
        };
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного заказа:",
          error
        );
      }
    },
  },

  async mounted() {
    const store = useOrderStore();
    await store.fetchOrders();
    await this.fetchPreselectedOption();
  },
};
</script>
